import React, { useEffect, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import Select from 'react-select';
import Geosuggest from '@ubilabs/react-geosuggest';
import { useDispatch, useSelector } from 'react-redux';
import { getRequest } from '../../../requestUtils';
import MultiSelectInput from '../../common/MultiSelectInput';
import profileSuggestionsActions from '../../../actions/profileSuggestionsActions';
import { analyticsTrack } from '../../../analyticsUtils';
import allActions from '../../../actions';

const getCurrentQuestionsFromKeys = (
  questionKeys,
  questionableId,
  questionableType,
  questionnaireName,
) => getRequest(
  '/question_answers/questions',
  {
    questionnaire_question_keys: questionKeys,
    questionable_id: questionableId,
    questionable_type: questionableType,
    questionnaire_name: questionnaireName,
  },
);

function SelectableItem({
  onEdit,
  onToggle,
  checked = true,
  itemEditable = true,
  child,
  error,
}) {
  return (
    <div className="selectable-item">
      <div className="selectable">
        <div>
          <input
            className="checkbox"
            type="checkbox"
            id="boolean"
            checked={checked}
            onChange={() => onToggle(!checked)}
          />
        </div>
        <div className={`child ${checked ? '' : 'unchecked'}`}>{ child }</div>
        { itemEditable
        && (
        <div
          className="edit"
          style={{ pointerEvents: checked ? 'all' : 'none' }}
          onClick={() => onEdit()}
          role="button"
          tabIndex="0"
        >
          <i className="fas fa-pencil-alt" />
        </div>
        )}
      </div>
      { error && (
      <div className="error">
        { error }
        {' '}
      </div>
      )}
    </div>
  );
}

SelectableItem.propTypes = {
  onEdit: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  checked: PropTypes.bool,
  itemEditable: PropTypes.bool,
  child: PropTypes.node,
  error: PropTypes.string,
};

SelectableItem.defaultProps = {
  checked: true,
  itemEditable: true,
  child: null,
  error: null,
};

function Summary({
  userId, hasChanged, accepted, bio, onUpdate, onToggle,
}) {
  const [modalSummaryValue, setModalSummaryValue] = useState(bio);
  const [isEditModalVisible, setEditModalVisibility] = useState(false);

  useEffect(() => {
    setModalSummaryValue(bio);
  }, [bio]);

  const update = () => {
    setEditModalVisibility(false);
    onUpdate(modalSummaryValue);
  };

  const edit = () => {
    setEditModalVisibility(true);
    analyticsTrack('Editing bio', {
      userId,
      location: 'Onboarding Flow',
      sublocation: 'Profile Suggestions',
    });
  };

  return (
    <div className="bio">
      <div className="wrapper">
        <h3 className="title">Bio</h3>
      </div>
      <SelectableItem
        onToggle={onToggle}
        onEdit={() => edit()}
        checked={accepted}
        child={<div>{ bio }</div>}
      />
      { hasChanged
        && <div className="has-changed">Adding this BIO will override your current bio</div>}
      <Modal show={isEditModalVisible} onHide={() => setEditModalVisibility(false)}>
        <Modal.Header closeButton />
        <Modal.Body className="edit-suggestion">
          <div className="body">
            <header className="header">Edit Summary</header>
            <div className="description">What is your new bio?</div>
            <div className="input-field">
              <div className="input-label">Summary</div>
              <textarea
                className="input-base"
                rows="4"
                placeholder="Write here..."
                value={modalSummaryValue}
                required={false}
                onChange={(v) => setModalSummaryValue(v.target.value)}
              />
            </div>
          </div>
          <div className="buttons-bar">
            <button className="save" onClick={() => update()}>
              Save Changes
            </button>
            <button className="cancel" onClick={() => setEditModalVisibility(false)}>
              Cancel
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}

Summary.propTypes = {
  userId: PropTypes.string.isRequired,
  hasChanged: PropTypes.bool.isRequired,
  accepted: PropTypes.bool.isRequired,
  bio: PropTypes.string.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
};

function Location({
  userId, hasChanged, accepted, location, onUpdate, onToggle,
}) {
  const [modalLocationValue, setModalLocationValue] = useState(location);
  const [isEditModalVisible, setEditModalVisibility] = useState(false);

  useEffect(() => {
    setModalLocationValue(location);
  }, [location]);

  const update = () => {
    setEditModalVisibility(false);
    onUpdate(modalLocationValue);
  };

  const edit = () => {
    setEditModalVisibility(true);
    analyticsTrack('Editing location', {
      userId,
      location: 'Onboarding Flow',
      sublocation: 'Profile Suggestions',
    });
  };

  const clear = () => {
    setModalLocationValue('');
  };

  return (
    <div className="location">
      <div className="wrapper">
        <h3 className="title">Location</h3>
      </div>
      <SelectableItem
        onToggle={onToggle}
        onEdit={() => edit()}
        checked={accepted}
        child={<div>{ location }</div>}
      />
      { hasChanged
      && <div className="has-changed">Adding this Location will override your current location</div>}
      <div />
      <Modal show={isEditModalVisible} onHide={() => setEditModalVisibility(false)}>
        <Modal.Header closeButton />
        <Modal.Body className="edit-suggestion">
          <div className="body">
            <header className="header">Edit Location</header>
            <div className="description">What is your new location?</div>
            <div className="input-field">
              <div className="input-label">City or postal code</div>
              <div className="input-group">
                <Geosuggest
                  initialValue={modalLocationValue}
                  suggestItemClassName="geosuggest-font"
                  inputClassName="input-base"
                  placeholder="Type your current location"
                  onSuggestSelect={(obj) => obj && setModalLocationValue(obj.label)}
                />
                <button onClick={() => clear()}>
                  <i className="fas fa-times" />
                </button>
              </div>
            </div>
          </div>
          <div className="buttons-bar">
            <button className="save" onClick={() => update()}>
              Save Changes
            </button>
            <button className="cancel" onClick={() => setEditModalVisibility(false)}>
              Cancel
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}

Location.propTypes = {
  userId: PropTypes.string.isRequired,
  hasChanged: PropTypes.bool.isRequired,
  accepted: PropTypes.bool.isRequired,
  location: PropTypes.string.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
};

const isDate = (date) => {
  if (!date) {
    return false;
  }
  const convertedDate = new Date(date);
  return (convertedDate !== 'Invalid Date') && !isNaN(convertedDate);
};

const yearOptions = () => (
  <Fragment>
    <option key="select" value="">Select year</option>
    {Array(new Date().getFullYear() - 1969).fill()
      .map((_, idx) => 1970 + idx)
      .map((i) => <option key={i} value={i}>{i}</option>)}
  </Fragment>
);

function EducationItem({ education }) {
  let startDate = '';
  if (Object.prototype.hasOwnProperty.call(education, 'startDate') && isDate(education.startDate)) {
    startDate = new Date(education.startDate).getFullYear();
  }

  let endDate = '';
  if (Object.prototype.hasOwnProperty.call(education, 'endDate') && isDate(education.endDate)) {
    endDate = new Date(education.endDate).getFullYear();
  }

  let degreeLabel = '';
  if (Object.prototype.hasOwnProperty.call(education, 'degree') && typeof education.degree === 'object'
    && education.degree != null && Object.prototype.hasOwnProperty.call(education.degree, 'label')) {
    degreeLabel = education.degree.label;
  } else {
    degreeLabel = education.degree;
  }

  return (
    <div className="item">
      <div className="text text-1">{ degreeLabel }</div>
      <div className="text text-2">{ education.school}</div>
      <div className="text text-3">
        { startDate }
        {' '}
        -
        {' '}
        { endDate }
      </div>
    </div>
  );
}

EducationItem.propTypes = {
  education: PropTypes.shape({}).isRequired,
};

function Education({
  userId,
  hasChanged,
  education,
  onUpdate,
  onToggle,
  question,
  errors,
}) {
  const [isEditModalVisible, setEditModalVisibility] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingIndex, setEditingIndex] = useState(-1);

  const updateItem = () => {
    onUpdate(editingIndex, editingItem);
    setEditModalVisibility(false);
    setEditingIndex(-1);
    setEditingItem(null);
  };

  const clearItem = () => {
    setEditingItem({
      degree: '',
      areaOfStudy: '',
      school: '',
      startDate: '',
      endDate: '',
    });
    onToggle(editingIndex, false);
  };

  const onEditItem = (index) => {
    setEditingItem(education[index].education);
    setEditingIndex(index);
    setEditModalVisibility(true);

    analyticsTrack('Editing education', {
      index,
      userId,
      location: 'Onboarding Flow',
      sublocation: 'Profile Suggestions',
    });
  };

  const renderEditModal = () => {
    if (editingItem === null) {
      return null;
    }

    const className = [
      question.children_questions[0].question_type,
      question.children_questions[0].style,
      question.children_questions[0].question_key,
    ].join(' ');
    return (
      <Modal show={isEditModalVisible} onHide={() => setEditModalVisibility(false)}>
        <Modal.Header closeButton />
        <Modal.Body className="edit-suggestion">
          <div className="body">
            <header className="header">Edit Education</header>
            <div className="input-field">
              <div className="input-label">Degree</div>
              <Select
                components={{
                  IndicatorSeparator: () => null,
                  DropdownIndicator: () => <i className="fas fa-angle-down" />,
                }}
                className={className}
                classNamePrefix="react-select"
                placeholder="Select or type..."
                options={question.children_questions[0].options}
                value={editingItem.degree}
                onChange={(option) => setEditingItem({ ...editingItem, degree: option })}
                isDisabled={false}
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
            <div className="input-field">
              <div className="input-label">Area of Study</div>
              <input
                type="text"
                className="input-base"
                placeholder="Write here..."
                value={editingItem.areaOfStudy}
                required={false}
                onChange={(v) => setEditingItem({ ...editingItem, areaOfStudy: v.target.value })}
              />
            </div>
            <div className="input-field">
              <div className="input-label">School</div>
              <input
                type="text"
                className="input-base"
                placeholder="Write here..."
                value={editingItem.school}
                required={false}
                onChange={(v) => setEditingItem({ ...editingItem, school: v.target.value })}
              />
            </div>
            <div className="one-row">
              <div className="input-field">
                <div className="input-label">Year started</div>
                <select
                  className="input-base"
                  onChange={(v) => setEditingItem({ ...editingItem, startDate: `${v.target.value}-01-01` })}
                  value={isDate(editingItem.startDate)
                    ? window.moment(editingItem.startDate).toDate().getFullYear() : ''}
                >
                  { yearOptions() }
                </select>
              </div>
              <div className="input-field">
                <div className="input-label">Year done</div>
                <select
                  className="input-base"
                  onChange={(v) => setEditingItem({ ...editingItem, endDate: `${v.target.value}-01-01` })}
                  value={isDate(editingItem.endDate)
                    ? window.moment(editingItem.endDate).toDate().getFullYear() : ''}
                >
                  { yearOptions() }
                </select>
              </div>
            </div>
          </div>
          <div className="buttons-bar">
            <button className="save" onClick={() => updateItem()}>
              Save Changes
            </button>
            <button className="clear" onClick={() => clearItem()}>
              <i className="fas fa-trash-alt" />
              Remove
            </button>
            <button className="cancel" onClick={() => setEditModalVisibility(false)}>
              Cancel
            </button>
          </div>
        </Modal.Body>
      </Modal>
    );
  };

  return (
    <div className="education">
      <div className="wrapper">
        <h3 className="title">Education</h3>
      </div>
      <div className="block">
        { education.map((e, index) => (
          <SelectableItem
            key={`education-${index}`}
            onToggle={() => onToggle(index, !e.accepted)}
            onEdit={() => onEditItem(index)}
            error={errors[index]}
            checked={e.accepted}
            child={<EducationItem education={e.education} />}
          />
        ))}
      </div>
      { hasChanged
      && <div className="has-changed">Adding these Education will override your current Education</div>}
      { renderEditModal() }
    </div>
  );
}

Education.propTypes = {
  userId: PropTypes.string.isRequired,
  hasChanged: PropTypes.bool.isRequired,
  education: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onUpdate: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  question: PropTypes.shape({}).isRequired,
  errors: PropTypes.arrayOf(PropTypes.string).isRequired,
};

function PastRoleItem({ pastRole }) {
  let startDate = '';
  if (isDate(pastRole.startDate)) {
    startDate = new Date(pastRole.startDate).getFullYear();
  }

  let endDate = '';
  if (isDate(pastRole.endDate)) {
    endDate = new Date(pastRole.endDate).getFullYear();
  }

  return (
    <div className="item">
      <div className="text text-1">{ pastRole.title }</div>
      <div className="text text-2">{ pastRole.companyName }</div>
      <div className="text text-3">
        { startDate }
        {' '}
        -
        {' '}
        { endDate }
      </div>
      <div className="text text-4">{ pastRole.description }</div>
    </div>
  );
}

PastRoleItem.propTypes = {
  pastRole: PropTypes.shape({}).isRequired,
};

function PastRoles({
  userId, hasChanged, pastRoles, onUpdate, onToggle,
}) {
  const [isEditModalVisible, setEditModalVisibility] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingIndex, setEditingIndex] = useState(-1);

  const updateItem = () => {
    onUpdate(editingIndex, editingItem);
    setEditModalVisibility(false);
    setEditingIndex(-1);
    setEditingItem(null);
  };

  const clearItem = () => {
    setEditingItem({
      title: '',
      companyName: '',
      startDate: '',
      endDate: '',
      description: '',
    });
    onToggle(editingIndex, false);
  };

  const onEditItem = (index) => {
    setEditingItem(pastRoles[index].pastRole);
    setEditingIndex(index);
    setEditModalVisibility(true);

    analyticsTrack('Editing Past roles', {
      index,
      userId,
      location: 'Onboarding Flow',
      sublocation: 'Profile Suggestions',
    });
  };

  const renderEditModal = () => {
    if (editingItem === null) {
      return null;
    }

    return (
      <Modal show={isEditModalVisible} onHide={() => setEditModalVisibility(false)}>
        <Modal.Header closeButton />
        <Modal.Body className="edit-suggestion">
          <div className="body">
            <header className="header">Edit Past Role</header>
            <div className="input-field">
              <div className="input-label">Title</div>
              <input
                type="text"
                className="input-base"
                placeholder="Write here..."
                value={editingItem.title}
                required={false}
                onChange={(v) => setEditingItem({ ...editingItem, title: v.target.value })}
              />
            </div>
            <div className="input-field">
              <div className="input-label">Company name</div>
              <input
                type="text"
                className="input-base"
                placeholder="Write here..."
                value={editingItem.companyName}
                required={false}
                onChange={(v) => setEditingItem({ ...editingItem, companyName: v.target.value })}
              />
            </div>
            <div className="one-row">
              <div className="input-field">
                <div className="input-label">Year started</div>
                <select
                  className="input-base"
                  onChange={(v) => setEditingItem({ ...editingItem, startDate: `${v.target.value}-01-01` })}
                  value={isDate(editingItem.startDate)
                    ? window.moment(editingItem.startDate).toDate().getFullYear() : ''}
                >
                  { yearOptions() }
                </select>
              </div>
              <div className="input-field">
                <div className="input-label">Year done</div>
                <select
                  className="input-base"
                  onChange={(v) => setEditingItem({ ...editingItem, endDate: `${v.target.value}-01-01` })}
                  value={isDate(editingItem.endDate)
                    ? window.moment(editingItem.endDate).toDate().getFullYear() : ''}
                >
                  { yearOptions() }
                </select>
              </div>
            </div>
            <div className="one-row">
              <div className="input-field">
                <div className="input-label">Description</div>
                <textarea
                  rows="5"
                  type="text"
                  className="input-base"
                  placeholder="Write here..."
                  value={editingItem.description}
                  required={false}
                  onChange={(v) => setEditingItem({ ...editingItem, description: v.target.value })}
                />
              </div>
            </div>
          </div>
          <div className="buttons-bar">
            <button className="save" onClick={() => updateItem()}>
              Save Changes
            </button>
            <button className="clear" onClick={() => clearItem()}>
              <i className="fas fa-trash-alt" />
              Remove
            </button>
            <button className="cancel" onClick={() => setEditModalVisibility(false)}>
              Cancel
            </button>
          </div>
        </Modal.Body>
      </Modal>
    );
  };

  return (
    <div className="past-roles">
      <div className="wrapper">
        <h3 className="title">Past Roles</h3>
      </div>
      <div className="block">
        { pastRoles.map((e, index) => (
          e.pastRole && (e.pastRole.companyName || e.pastRole.title || e.pastRole.description)
          && (
          <SelectableItem
            key={`pastRole-${index}`}
            onToggle={() => onToggle(index, !e.accepted)}
            onEdit={() => onEditItem(index)}
            checked={e.accepted}
            child={<PastRoleItem pastRole={e.pastRole} />}
          />
          )
        ))}
      </div>
      { hasChanged
      && (
      <div className="has-changed">
        Adding these Past roles will override your current Past Roles
      </div>
      )}
      { renderEditModal() }
    </div>
  );
}

PastRoles.propTypes = {
  userId: PropTypes.string.isRequired,
  hasChanged: PropTypes.bool.isRequired,
  pastRoles: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onUpdate: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
};

const TAGs = [
  { id: 'functionalAreas', name: 'Functional Areas', key: 'functional_areas' },
  { id: 'therapeuticAreas', name: 'Therapeutic Areas', key: 'therapeutic_areas' },
  { id: 'industries', name: 'Industries', key: 'industries' },
  { id: 'stagesOfDevelopments', name: 'Stages Of Development', key: 'stages_of_development' },
  { id: 'productTechnologies', name: 'Product Technologies', key: 'product_technologies' },
];

function TagItem({ tag }) {
  return (
    <div className="item">
      <div className="text text-1">{tag.label}</div>
    </div>
  );
}

TagItem.propTypes = {
  tag: PropTypes.shape({}).isRequired,
};

function Tags({
  userId, id, name, hasChanged, question, tags, onUpdate,
}) {
  const [isEditModalVisible, setEditModalVisibility] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [values, setValues] = useState([]);

  useEffect(() => {
    const initialTags = tags.filter((t) => t.accepted).map((t) => t[id.slice(0, id.length - 1)]).flat();
    setValues(initialTags);
  }, [tags, question]);

  const updateItem = () => {
    onUpdate(
      id,
      values.map((v) => (
        { accepted: true, [id.slice(0, id.length - 1)]: { name: v.label, user_created: false } }
      )),
    );
    setEditModalVisibility(false);
  };

  const onToggleItem = (index, accepted) => {
    const newTags = tags.map((e, ix) => {
      if (ix === index) {
        return {
          ...e,
          accepted,
        };
      }
      return e;
    });

    const field = id.slice(0, id.length - 1);
    let label;
    try {
      label = tags[index].label;
    } catch (error) {
      label = `Tag:${id}:Label:${index}`;
    }
    analyticsTrack(`Accepting/Declining ${name.slice(0, name.length - 1)}`, {
      location: 'Onboarding Flow',
      sublocation: 'Profile Suggestions',
      action: accepted ? 'Accepting' : 'Declining',
      item: label,
      userId,
    });
    onUpdate(
      id,
      newTags.map((t) => ({ ...t, [field]: { name: t[field].label, user_created: false } })),
    );
  };

  const clearItem = () => {
    setEditingItem({
      ...editingItem,
      name: '',
    });
  };

  const onEdit = () => {
    analyticsTrack(`Editing ${name.slice(0, name.length - 1)}`, {
      userId,
      location: 'Onboarding Flow',
      sublocation: 'Profile Suggestions',
    });
    setEditModalVisibility(true);
  };

  const renderEditModal = () => {
    if (!question) {
      return null;
    }

    const {
      subtext,
      answer_options: options,
      async_url: asyncUrl,
      disabled,
      has_other: hasOther,
      has_filter: hasFilter,
      question_type: questionType,
      style,
      limit,
      key,
    } = question;

    return (
      <Modal
        dialogClassName="tags-modal"
        show={isEditModalVisible}
        onHide={() => setEditModalVisibility(false)}
      >
        <Modal.Header closeButton />
        <Modal.Body className="edit-suggestion">
          <div className="body question new-design">
            <header className="header">
              Edit
              {name}
            </header>
            <MultiSelectInput
              subtext={subtext}
              options={options}
              values={values}
              handleSelectChange={(option) => setValues(option)}
              asyncUrl={asyncUrl}
              disabled={disabled}
              hasOther={hasOther}
              hasFilter={hasFilter}
              style={style}
              limit={limit}
              onlyCustom={questionType === 'only_custom_tags'}
              onboardingQuestionnaire={false}
              questionKey={key}
              text={name}
            />
          </div>
          <div className="buttons-bar">
            <button className="save" onClick={() => updateItem()}>
              Save Changes
            </button>
            <button className="clear" onClick={() => clearItem()}>
              Clear
            </button>
            <button className="cancel" onClick={() => setEditModalVisibility(false)}>
              Cancel
            </button>
          </div>
        </Modal.Body>
      </Modal>
    );
  };

  return (
    <div className={id}>
      <div className="wrapper">
        <h3 className="title">{ name }</h3>
        <div className="edit" onClick={() => onEdit()} role="button" tabIndex="0">
          <i className="fas fa-pencil-alt" />
        </div>
      </div>
      <div className="block">
        { (tags.filter((item) => (item[id.slice(0, id.length - 1)]))).map((item, index) => (
          <SelectableItem
            key={`${id}-${index}`}
            checked={item.accepted}
            itemEditable={false}
            onToggle={() => onToggleItem(index, !item.accepted)}
            child={<TagItem tag={item[id.slice(0, id.length - 1)]} />}
          />
        ))}
      </div>
      { hasChanged
      && (
      <div className="has-changed">
        Adding these
        { name }
        {' '}
        will override your current
        { name }
      </div>
      )}
      { renderEditModal() }
    </div>
  );
}

Tags.propTypes = {
  userId: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  hasChanged: PropTypes.bool.isRequired,
  question: PropTypes.shape({}).isRequired,
  tags: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onUpdate: PropTypes.func.isRequired,
};

function ProfileSuggestions({
  userId,
  questionnaireName,
  questionableId,
  questionableType,
  fromOnboarding,
}) {
  const {
    profileSuggestions,
    hasChanged,
    errors: errorsProfileSuggestions,
  } = useSelector((state) => state.profileSuggestions);
  const dispatch = useDispatch();
  const resumeOnly = fromOnboarding ? '' : 'resume_only=true';
  const getProfileSuggestions = () => getRequest(`/users/${userId}/profile_suggestions?${resumeOnly}`);

  const [currentSuggestion, setCurrentSuggestion] = useState(null);
  const [tagsQuestions, setTagsQuestions] = useState([]);
  const [educationQuestion, setEducationQuestion] = useState(null);

  useEffect(() => {
    const getData = async () => {
      const response = await getProfileSuggestions(userId);
      const { suggestions, answers, changed } = response.data;

      dispatch(allActions.profileSuggestions.profileSuggestionsParsed({
        suggestions,
        changed,
        answers,
      }));
    };
    if (!fromOnboarding) {
      getData();
    }
  }, []);

  useEffect(() => {
    let {
      functionalAreas,
      therapeuticAreas,
      industries,
      productTechnologies,
      stagesOfDevelopments,
    } = profileSuggestions.merged;

    if (profileSuggestions.updated) {
      analyticsTrack('Is getting suggestions', {
        location: fromOnboarding ? 'Onboarding Flow' : 'Consultant Onboarding',
        sublocation: 'Profile Suggestions',
        userId,
      });
    }
    if (tagsQuestions.length > 0) {
      // Functional Areas
      let question = tagsQuestions.find((q) => q.question_key === 'functional_areas');
      functionalAreas = functionalAreas.map((fa) => (
        {
          ...fa,
          functionalArea: question.answer_options.find((opt) => opt.label === fa.functionalArea.name),
        }
      ));
      // Therapeutic Areas
      question = tagsQuestions.find((q) => q.question_key === 'therapeutic_areas');
      therapeuticAreas = therapeuticAreas.map((ta) => (
        {
          ...ta,
          therapeuticArea: question.answer_options.find(
            (opt) => opt.label === ta.therapeuticArea.name,
          ),
        }
      ));
      // Industries
      question = tagsQuestions.find((q) => q.question_key === 'industries');
      industries = industries.map((i) => (
        {
          ...i,
          industrie: question.answer_options.find((opt) => opt.label === i.industrie.name),
        }
      ));
      // Product Technologies
      question = tagsQuestions.find((q) => q.question_key === 'product_technologies');
      productTechnologies = productTechnologies.map((pt) => (
        {
          ...pt,
          productTechnologie: question.answer_options.find(
            (opt) => opt.label === pt.productTechnologie.name,
          ),
        }
      ));
      // Stages Of Development
      question = tagsQuestions.find((q) => q.question_key === 'stages_of_development');
      stagesOfDevelopments = stagesOfDevelopments.map((sod) => (
        {
          ...sod,
          stagesOfDevelopment: question.answer_options.find(
            (opt) => opt.label === sod.stagesOfDevelopment.name,
          ),
        }
      ));
    }

    setCurrentSuggestion({
      ...profileSuggestions.merged,
      functionalAreas,
      therapeuticAreas,
      industries,
      productTechnologies,
      stagesOfDevelopments,
    });
  }, [profileSuggestions, tagsQuestions]);

  useEffect(() => {
    const getQuestionDetails = async () => {
      const response = await getCurrentQuestionsFromKeys(['functional_areas', 'therapeutic_areas', 'industries', 'stages_of_development', 'product_technologies', 'education'], questionableId, questionableType, questionnaireName);

      setEducationQuestion(response.data.find((q) => q.question_key === 'education'));
      setTagsQuestions(response.data.filter((q) => q.question_key !== 'education'));
    };

    getQuestionDetails();
  }, [questionnaireName, questionableId, questionableType]);

  const onToggleSummary = (accepted) => {
    const { bio } = currentSuggestion;
    const updatedBio = {
      ...bio,
      accepted,
    };

    analyticsTrack('Accepting/Declining bio', {
      location: 'Onboarding Flow',
      sublocation: 'Profile Suggestions',
      userId,
      action: accepted ? 'Accepting' : 'Declining',
    });
    dispatch(profileSuggestionsActions.updateBio(updatedBio));
  };

  const onUpdateSummary = (newSummary) => {
    const { bio } = currentSuggestion;
    const updatedBio = {
      ...bio,
      bio: newSummary,
    };

    analyticsTrack('Updating bio', {
      userId,
      location: 'Onboarding Flow',
      sublocation: 'Profile Suggestions',
    });

    dispatch(profileSuggestionsActions.updateBio(updatedBio));
  };

  const onToggleLocation = (accepted) => {
    const { location } = currentSuggestion;

    analyticsTrack('Accepting/Declining location', {
      location: 'Onboarding Flow',
      sublocation: 'Profile Suggestions',
      userId,
      action: accepted ? 'Accepting' : 'Declining',
    });

    const updatedLocation = {
      ...location,
      accepted,
    };

    dispatch(profileSuggestionsActions.updateLocation(updatedLocation));
  };

  const onUpdateLocation = (newLocation) => {
    const { location } = currentSuggestion;

    analyticsTrack('Updating location', {
      userId,
      location: 'Onboarding Flow',
      sublocation: 'Profile Suggestions',
    });

    const updatedLocation = {
      ...location,
      location: newLocation,
    };

    dispatch(profileSuggestionsActions.updateLocation(updatedLocation));
  };

  const onToggleEducation = (index, accepted) => {
    const { education } = currentSuggestion;
    const newEducations = education.map((e, ix) => {
      if (ix === index) {
        return {
          ...e,
          accepted,
        };
      }
      return e;
    });

    analyticsTrack('Accepting/Declining education', {
      location: 'Onboarding Flow',
      sublocation: 'Profile Suggestions',
      index,
      userId,
      action: accepted ? 'Accepting' : 'Declining',
    });

    dispatch(profileSuggestionsActions.updateArray('education', newEducations));
  };

  const onUpdateEducation = (index, newEducation) => {
    const { education } = currentSuggestion;

    const newEducations = education.map((e, ix) => {
      if (ix === index) {
        return {
          ...e,
          education: newEducation,
        };
      }
      return e;
    });

    analyticsTrack('Updating education', {
      index,
      userId,
      location: 'Onboarding Flow',
      sublocation: 'Profile Suggestions',
    });

    dispatch(profileSuggestionsActions.updateArray('education', newEducations));
  };

  const onTogglePastRole = (index, accepted) => {
    const { pastRoles } = currentSuggestion;

    const newPastRoles = pastRoles.map((e, ix) => {
      if (ix === index) {
        return {
          ...e,
          accepted,
        };
      }
      return e;
    });

    analyticsTrack('Accepting/Declining Past roles', {
      location: 'Onboarding Flow',
      sublocation: 'Profile Suggestions',
      index,
      userId,
      action: accepted ? 'Accepting' : 'Declining',
    });

    dispatch(profileSuggestionsActions.updateArray('pastRoles', newPastRoles));
  };

  const onUpdatePastRole = (index, newPastRole) => {
    const { pastRoles } = currentSuggestion;

    const newPastRoles = pastRoles.map((e, ix) => {
      if (ix === index) {
        return {
          ...e,
          pastRole: newPastRole,
        };
      }
      return e;
    });

    dispatch(profileSuggestionsActions.updateArray('pastRoles', newPastRoles));
  };

  const onUpdateTag = (id, newTagsModified) => {
    let name;
    try {
      name = TAGs.find((t) => t.id === id).name.slice(0, name.length - 1);
    } catch (error) {
      name = `TAG:${id}`;
    }

    analyticsTrack(`Updating ${name}`, {
      userId,
      location: 'Onboarding Flow',
      sublocation: 'Profile Suggestions',
    });

    dispatch(profileSuggestionsActions.updateArray(id, newTagsModified));
  };

  if (!currentSuggestion || !profileSuggestions.updated) {
    return null;
  }

  if (!(currentSuggestion.bio.bio || currentSuggestion.location.location
    || currentSuggestion.education.length || currentSuggestion.pastRoles.length
    || TAGs.reduce((total, tag) => (total + (currentSuggestion[tag.id] || []).length), 0) > 0
  )) {
    return null;
  }

  return (
    <section className={`suggestion-card ${fromOnboarding ? '' : 'consultant-profile'}`}>
      <header>
        <h2 className="title">We found some profile suggestions for you!</h2>
        <div className="description">
          Checked items will be added to your profile. You can pick and edit
          the items you would like to include in your Clora profile
        </div>
      </header>

      { currentSuggestion.bio.bio
        && (
        <Summary
          userId={userId}
          hasChanged={!fromOnboarding && hasChanged.bio}
          {...currentSuggestion.bio}
          onUpdate={(v) => onUpdateSummary(v)}
          onToggle={(v) => onToggleSummary(v)}
        />
        )}
      { currentSuggestion.location.location
        && (
        <Location
          userId={userId}
          hasChanged={!fromOnboarding && hasChanged.location}
          {...currentSuggestion.location}
          onUpdate={(v) => onUpdateLocation(v)}
          onToggle={(v) => onToggleLocation(v)}
        />
        )}
      { currentSuggestion.education.length !== 0
        && (
        <Education
          userId={userId}
          hasChanged={!fromOnboarding && hasChanged.education}
          education={currentSuggestion.education}
          onUpdate={onUpdateEducation}
          onToggle={onToggleEducation}
          question={educationQuestion}
          errors={errorsProfileSuggestions.education}
        />
        )}
      { currentSuggestion.pastRoles.length !== 0
        && (
        <PastRoles
          userId={userId}
          hasChanged={!fromOnboarding && hasChanged.pastRoles}
          pastRoles={currentSuggestion.pastRoles}
          onUpdate={onUpdatePastRole}
          onToggle={onTogglePastRole}
        />
        )}
      { TAGs.map((tag) => currentSuggestion[tag.id].length !== 0 && (
      <Tags
        userId={userId}
        id={tag.id}
        hasChanged={!fromOnboarding && hasChanged[tag.id]}
        name={tag.name || `TAG:${tag.id}:s`}
        tags={currentSuggestion[tag.id]}
        onUpdate={onUpdateTag}
        question={tagsQuestions.find((q) => q.question_key === tag.key)}
      />
      ))}
    </section>
  );
}

ProfileSuggestions.propTypes = {
  userId: PropTypes.string.isRequired,
  questionableId: PropTypes.number.isRequired,
  questionableType: PropTypes.string.isRequired,
  questionnaireName: PropTypes.string.isRequired,
  fromOnboarding: PropTypes.bool,
};

ProfileSuggestions.defaultProps = {
  fromOnboarding: true,
};

export default ProfileSuggestions;
